import { useEffect, useMemo } from "react";
import {
  Vector2,
  Vector3,
  PlaneGeometry,
  InstancedBufferGeometry,
  InstancedBufferAttribute,
  ShaderMaterial,
  DataTexture,
  DataArrayTexture,
  RGBAFormat,
  RedFormat,
  FrontSide,
  HalfFloatType,
  NearestFilter,
} from "three";
import { useThree } from "@react-three/fiber";
import { scaleSequential } from "d3-scale";
import { interpolateTurbo } from "d3-scale-chromatic";
import { rgb } from "d3-color";
import { useAtom } from "jotai";

import { type View } from "_/state/viewer";
import { INVALID_SENSOR_TEXTURE_VALUE } from "_/components/analyze/sensor-constants";

import { normalizeValue } from "_/utils";

import billboardVert from "./glsl/billboard.vert";
import billboardFrag from "./glsl/billboard.frag";

function generateLookupTexture() {
  // Color scale used to map sensor values to colors
  const scale = scaleSequential(interpolateTurbo).domain([0, 1]);

  // Generate a lookup texture to use the color scale in the shader
  const textureWidth = 256;
  const data = new Uint8Array(textureWidth * 4); // RGBA

  for (let i = 0; i < textureWidth; i++) {
    const color = rgb(scale(i / textureWidth));
    const offset = i * 4;
    data[offset] = color.r;
    data[offset + 1] = color.g;
    data[offset + 2] = color.b;
    data[offset + 3] = 255;
  }

  const lookupTexture = new DataTexture(data, textureWidth, 1, RGBAFormat);
  lookupTexture.needsUpdate = true;
  return lookupTexture;
}

type PrintViewProps = {
  view: View<"analyze">;
};

export const PrintView = ({ view }: PrintViewProps) => {
  const { invalidate } = useThree();

  const [viewControls] = useAtom(view.controlsAtom);

  const {
    zRange,
    valueRange,
    colorRange,
    lineWidth,
    showTool0,
    showTool1,
    showNonExtrusionMotion,
  } = viewControls;

  const instancedGeometry = useMemo(() => {
    const baseGeometry = new PlaneGeometry(1, 1);

    const instancedGeometryInner = new InstancedBufferGeometry();
    instancedGeometryInner.index = baseGeometry.index;
    instancedGeometryInner.attributes.position =
      baseGeometry.attributes.position;
    instancedGeometryInner.attributes.uv = baseGeometry.attributes.uv;
    instancedGeometryInner.attributes.normal = baseGeometry.attributes.normal;

    const { startPoints, endPoints, offsets, counts, extrusionValues } =
      view.data;

    instancedGeometryInner.setAttribute(
      "startVertex",
      new InstancedBufferAttribute(startPoints, 3)
    );

    instancedGeometryInner.setAttribute(
      "endVertex",
      new InstancedBufferAttribute(endPoints, 3)
    );

    instancedGeometryInner.setAttribute(
      "offset",
      new InstancedBufferAttribute(offsets, 1)
    );

    instancedGeometryInner.setAttribute(
      "count",
      new InstancedBufferAttribute(counts, 1)
    );

    instancedGeometryInner.setAttribute(
      "extrusionValue",
      new InstancedBufferAttribute(extrusionValues, 1)
    );

    instancedGeometryInner.setAttribute(
      "segmentTool",
      new InstancedBufferAttribute(view.data.segmentTools, 1)
    );

    instancedGeometryInner.instanceCount = counts.length;

    return instancedGeometryInner;
  }, [view.data]);

  const material = useMemo(() => {
    const { sensorValues, sensorTextureDimensions } = view.data;

    const [sensorTextureWidth, sensorTextureHeight, sensorTextureDepth] =
      sensorTextureDimensions;

    const sensorArrayTexture = new DataArrayTexture(
      sensorValues,
      sensorTextureWidth,
      sensorTextureHeight,
      sensorTextureDepth
    );

    sensorArrayTexture.format = RedFormat;
    sensorArrayTexture.type = HalfFloatType;

    sensorArrayTexture.minFilter = NearestFilter;
    sensorArrayTexture.magFilter = NearestFilter;

    sensorArrayTexture.needsUpdate = true;

    return new ShaderMaterial({
      uniforms: {
        lineWidth: { value: lineWidth.range[1] / 2 },
        lookupTexture: { value: generateLookupTexture() },
        sensorArrayTexture: { value: sensorArrayTexture },
        sensorTextureDimensions: {
          value: new Vector3(
            sensorTextureWidth,
            sensorTextureHeight,
            sensorTextureDepth
          ),
        },
        zRange: { value: new Vector2() },
        valueRange: {
          value: new Vector2(),
        },
        colorLimit: {
          value: new Vector2(),
        },
        invalidSensorValue: {
          value: INVALID_SENSOR_TEXTURE_VALUE,
        },
        showTool0: { value: true },
        showTool1: { value: true },
        showNonExtrusionMotion: {
          value: false,
        },
      },
      vertexShader: billboardVert,
      fragmentShader: billboardFrag,
      side: FrontSide,
      transparent: false,
    });
  }, [view.data, lineWidth.range]);

  useEffect(() => {
    return () => {
      instancedGeometry.dispose();
      type Disposable = { dispose: () => void };
      // threejs doesn't have good type information for the uniforms, so cast.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      (material.uniforms.lookupTexture.value as Disposable).dispose();
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      (material.uniforms.sensorArrayTexture.value as Disposable).dispose();
      material.dispose();
    };
  }, [instancedGeometry, material]);

  // Update shader material uniforms based on control changes
  useEffect(() => {
    // We don't have good type information for the uniforms, so cast them.
    /* oxlint-disable typescript/no-unsafe-type-assertion */
    type XY = { x: number; y: number };
    const textureValue = (value: number) =>
      normalizeValue(value, view.data.info.sensorTextureDomain);
    (material.uniforms.zRange.value as XY).x = zRange.value[0];
    (material.uniforms.zRange.value as XY).y = zRange.value[1];
    (material.uniforms.valueRange.value as XY).x = textureValue(
      valueRange.value[0]
    );
    (material.uniforms.valueRange.value as XY).y = textureValue(
      valueRange.value[1]
    );
    (material.uniforms.colorLimit.value as XY).x = textureValue(
      colorRange.value[0]
    );
    (material.uniforms.colorLimit.value as XY).y = textureValue(
      colorRange.value[1]
    );
    /* oxlint-enable typescript/no-unsafe-type-assertion */
    material.uniforms.showTool0.value = showTool0;
    material.uniforms.showTool1.value = showTool1;
    material.uniforms.lineWidth.value = lineWidth.value;
    material.uniforms.showNonExtrusionMotion.value = showNonExtrusionMotion;
    invalidate();
  }, [
    zRange.value,
    valueRange.value,
    colorRange.value,
    lineWidth.value,
    showTool0,
    showTool1,
    showNonExtrusionMotion,
    material,
    view.data.info.sensorTextureDomain,
    invalidate,
  ]);

  return (
    <mesh
      geometry={instancedGeometry}
      material={material}
      frustumCulled={false}
    />
  );
};
